import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getRecipes, updateRecipe } from '@recipe/shared';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import toast from 'react-hot-toast';
import RecipeForm from '../components/RecipeForm';
import { calculateAndSaveNutrition } from '../utils/nutritionBackground';
import { uploadStepImage, deleteStepImage, uploadCoverImage } from '../utils/imageUpload';
import './AddRecipe.css';

const EditRecipe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    servings: 4,
    servingUnit: '份',
    category: '',
    parts: [{ name: '', steps: [{ ingredients: [{ name: '', weight: '' }], details: '' }] }]
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Check if user came from proper navigation (not browser back button)
    if (!location.state?.recipe) {
      toast.error('请从食谱页面导航到编辑页面');
      navigate('/');
      return;
    }

    const recipe = location.state.recipe;
    setFormData({
      title: recipe.title || '',
      servings: recipe.servings || 4,
      servingUnit: recipe.servingUnit || '份',
      category: recipe.category || '',
      parts: recipe.parts || [{ name: '', steps: [{ ingredients: [{ name: '', weight: '' }], details: '' }] }]
    });
  }, [location.state, navigate]);

  // Fetch existing categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const allRecipes = await getRecipes();
        const categorySet = new Set();
        (allRecipes || []).forEach((r) => {
          if (r.category && r.category.trim()) {
            categorySet.add(r.category.trim());
          }
        });
        setCategories(Array.from(categorySet).sort());
      } catch (error) {
        // Error fetching categories
      }
    };
    fetchCategories();
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      if (active.id.toString().startsWith('part-')) {
        const oldIndex = parseInt(active.id.split('-')[1]);
        const newIndex = parseInt(over.id.split('-')[1]);
        setFormData(prev => ({
          ...prev,
          parts: arrayMove(prev.parts, oldIndex, newIndex)
        }));
      } else if (active.id.toString().startsWith('step-')) {
        const [, partIndex, stepIndex] = active.id.split('-');
        const [, overPartIndex, overStepIndex] = over.id.split('-');
        if (partIndex === overPartIndex) {
          setFormData(prev => ({
            ...prev,
            parts: prev.parts.map((part, pIndex) =>
              pIndex === parseInt(partIndex)
                ? { ...part, steps: arrayMove(part.steps, parseInt(stepIndex), parseInt(overStepIndex)) }
                : part
            )
          }));
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePartChange = (partIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, index) =>
        index === partIndex ? { ...part, [field]: value } : part
      )
    }));
  };

  const handleStepChange = (partIndex, stepIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? {
            ...part,
            steps: part.steps.map((step, sIndex) =>
              sIndex === stepIndex ? { ...step, [field]: value } : step
            )
          }
          : part
      )
    }));
  };

  const handleIngredientChange = (partIndex, stepIndex, ingredientIndex, field, value) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        parts: prev.parts.map((part, pIndex) =>
          pIndex === partIndex
            ? {
              ...part,
              steps: part.steps.map((step, sIndex) =>
                sIndex === stepIndex
                  ? {
                    ...step,
                    ingredients: step.ingredients.map((ingredient, iIndex) =>
                      iIndex === ingredientIndex
                        ? { ...ingredient, [field]: value }
                        : ingredient
                    )
                  }
                  : step
              )
            }
            : part
        )
      };

      const currentStep = newFormData.parts[partIndex].steps[stepIndex];
      const lastIngredientIndex = currentStep.ingredients.length - 1;
      if (ingredientIndex === lastIngredientIndex && field === 'name' && value.trim() !== '') {
        newFormData.parts[partIndex].steps[stepIndex].ingredients.push({ name: '', weight: '' });
      }

      return newFormData;
    });
  };

  const removeIngredient = (partIndex, stepIndex, ingredientIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? {
            ...part,
            steps: part.steps.map((step, sIndex) =>
              sIndex === stepIndex
                ? { ...step, ingredients: step.ingredients.filter((_, iIndex) => iIndex !== ingredientIndex) }
                : step
            )
          }
          : part
      )
    }));
  };

  const addStep = (partIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, index) =>
        index === partIndex
          ? { ...part, steps: [...part.steps, { ingredients: [{ name: '', weight: '' }], details: '' }] }
          : part
      )
    }));
  };

  const removeStep = (partIndex, stepIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? { ...part, steps: part.steps.filter((_, sIndex) => sIndex !== stepIndex) }
          : part
      )
    }));
  };

  const addPart = () => {
    setFormData(prev => ({
      ...prev,
      parts: [...prev.parts, { name: '', steps: [{ ingredients: [{ name: '', weight: '' }], details: '' }] }]
    }));
  };

  const removePart = (partIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.filter((_, index) => index !== partIndex)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Auto-fill first part name with recipe title if empty
    if (formData.parts.length > 0 && !formData.parts[0].name.trim()) {
      formData.parts[0].name = formData.title;
    }

    const isValid = formData.title.trim() &&
      formData.parts.length > 0 &&
      formData.parts.every(part =>
        part.name.trim() &&
        part.steps.length > 0
      );

    if (!isValid) {
      toast.error('每个部分必须有名称和至少一个步骤');
      return;
    }

    setLoading(true);

    try {
      const recipeData = {
        title: formData.title,
        servings: formData.servings,
        servingUnit: formData.servingUnit,
        category: formData.category,
        coverImage: formData.coverImage || '',
        parts: formData.parts,
        nutritionFacts: null,
      };

      await updateRecipe(id, recipeData);
      toast.success('食谱更新成功！');
      navigate(`/recipe/${id}`, { replace: true });

      // Calculate nutrition facts in background
      calculateAndSaveNutrition(id, formData);
    } catch (error) {
      toast.error('更新食谱失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStepImageUpload = async (partIndex, stepIndex, file) => {
    try {
      const toastId = toast.loading('上传图片中...');
      const imageUrl = await uploadStepImage(file, id, partIndex, stepIndex);
      setFormData(prev => ({
        ...prev,
        parts: prev.parts.map((part, pIndex) =>
          pIndex === partIndex
            ? {
              ...part,
              steps: part.steps.map((step, sIndex) =>
                sIndex === stepIndex ? { ...step, imageUrl } : step
              )
            }
            : part
        )
      }));
      toast.dismiss(toastId);
      toast.success('图片上传成功');
    } catch (error) {
      toast.error('图片上传失败');
    }
  };

  const handleStepImageRemove = async (partIndex, stepIndex) => {
    const imageUrl = formData.parts[partIndex]?.steps[stepIndex]?.imageUrl;
    if (imageUrl) {
      await deleteStepImage(imageUrl);
    }
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? {
            ...part,
            steps: part.steps.map((step, sIndex) =>
              sIndex === stepIndex ? { ...step, imageUrl: '' } : step
            )
          }
          : part
      )
    }));
  };

  const handleCoverImageUpload = async (croppedDataUrl) => {
    setFormData(prev => ({ ...prev, coverImage: croppedDataUrl }));
    toast.success('封面图已设置');
  };

  const handleCoverImageRemove = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
  };

  return (
    <div className="container">
      <div className="add-recipe-page">
        <RecipeForm
          formData={formData}
          onInputChange={handleInputChange}
          onPartChange={handlePartChange}
          onStepChange={handleStepChange}
          onIngredientChange={handleIngredientChange}
          onAddPart={addPart}
          onRemovePart={removePart}
          onAddStep={addStep}
          onRemoveStep={removeStep}
          onRemoveIngredient={removeIngredient}
          onStepImageUpload={handleStepImageUpload}
          onStepImageRemove={handleStepImageRemove}
          onCoverImageUpload={handleCoverImageUpload}
          onCoverImageRemove={handleCoverImageRemove}
          categories={categories}
          onCategoryChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
          sensors={sensors}
          onDragEnd={handleDragEnd}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/recipe/${id}`)}
          loading={loading}
          submitLabel="更新食谱"
          loadingLabel="更新中..."
        />
      </div>
    </div>
  );
};

export default EditRecipe;
